import {
  MusicHomeCard,
  MusicHomeGroup,
  MusicHomeLessonCard,
  MusicHomePlaylistCard,
  MusicHomeResponse,
  MusicHomeSongCard,
  PlaylistChildCard
} from "../../../client/models/musicHome/MusicHomeResponse";
import {stringContainsTerm} from "../../../util/string-util";

const isCardAMatch = (card: MusicHomeCard, searchTerm: string): boolean => {
  switch (card.type) {
    case "song": // fall through
    case "lesson":
      if (stringContainsTerm(searchTerm, card.title) || stringContainsTerm(searchTerm, card.artistName)) {
        return true;
      }
      break;
    case "playlist":
      if (stringContainsTerm(searchTerm, card.title)) {
        return true;
      }
      break;
    default:
      return false;
  }

  return false;
}

const getMatchesFromCardSetForPlaylist = (searchTerm: string, cards: PlaylistChildCard[]): PlaylistChildCard[] => {
  const cardSet: PlaylistChildCard[] = [];

  if (!cards.length) {
    return cardSet;
  }

  for (const card of cards) {
    const clone = cloneCard(card);

    if (isCardAMatch(clone, searchTerm)) {
      cardSet.push(clone as PlaylistChildCard);
    }
  }

  return cardSet;
}

const getMatchesFromCardSet = (searchTerm: string, cards: MusicHomeCard[]): MusicHomeCard[] => {
  const cardSet: MusicHomeCard[] = [];

  if (!cards.length) {
    return cardSet;
  }

  for (const card of cards) {
    const clone = cloneCard(card);

    let cardIsAMatch = (isCardAMatch(clone, searchTerm));

    if (clone.type === "playlist") {
      const playlist = card as MusicHomePlaylistCard;
      clone.cards = getMatchesFromCardSetForPlaylist(searchTerm, playlist.cards ?? []);
      cardIsAMatch = cardIsAMatch || (clone.cards?.length ?? 0) > 0;
    }

    if (cardIsAMatch) {
      cardSet.push(clone);
    }
  }

  return cardSet;
}

export const filterMusicHomeResponse = (searchTerm: string, response: MusicHomeResponse): MusicHomeResponse => {
  if (!searchTerm) {
    return response;
  }

  const formattedSearchTerm = searchTerm.toLowerCase().trim();
  const result: { groups: MusicHomeGroup[] } = { groups: [] };

  for (const group of response.groups) {
    const groupMatches = getMatchesFromCardSet(formattedSearchTerm, group.cards);

    if (groupMatches.length > 0) {
      result.groups.push({
        id: group.id,
        isFeatured: group.isFeatured ?? false,
        title: group.title,
        cards: groupMatches
      });
    }
  }

  return result;
};

const cloneCard = (card: MusicHomeCard) => {
  switch (card.type) {
    case "song":
      return cloneSongCard(card);
    case "lesson":
      return cloneLessonCard(card);
    case "playlist":
      return clonePlaylistCard(card);
    default:
      throw new Error(`Unhandled card type`);
  }
}

const cloneSongCard = (card: MusicHomeSongCard): MusicHomeSongCard => {
  return {
    headline: card.headline,
    type: card.type,
    songId: card.songId,
    title: card.title,
    artistName: card.artistName,
    hasVideo: card.hasVideo,
    imageUrl: card.imageUrl,
    numberOfArrangements: card.numberOfArrangements
  };
}

const cloneLessonCard = (card: MusicHomeLessonCard): MusicHomeLessonCard => {
  return {
    headline: card.headline,
    type: card.type,
    lessonId: card.lessonId,
    title: card.title,
    artistName: card.artistName,
    hasVideo: card.hasVideo,
    imageUrl: card.imageUrl,
    levelDifficulty: card.levelDifficulty,
    difficultyIdentifier: card.difficultyIdentifier
  };
}

const clonePlaylistCard = (card: MusicHomePlaylistCard): MusicHomePlaylistCard => {
  return {
    headline: card.headline,
    type: card.type,
    playlistId: card.playlistId,
    title: card.title,
    imageUrl: card.imageUrl,
    subtext: card.subtext,
    difficultyIdentifier: card.difficultyIdentifier,
    cards: []
  };
}
